'use client'

import { AppWindow } from 'lucide-react'
import { type ReactNode, useSyncExternalStore } from 'react'

import { OrdoAppIcon } from '../logos/ordo-app-icon'
import { PhosphorIcon } from '../primitives/icon-picker'
import { sameEnvOrigin, sameEnvSlugOrigin } from '../same-env-origin'

// (no longer used) — the prior `lazy(() => import('../primitives/icon-picker'))`
// added a one-tick Suspense fallback flash on first paint. PhosphorIcon now
// renders synchronously.

// icon-picker is now imported synchronously: every consumer of this hook
// (notably HQ) also static-imports it via AppTileIcon / app-registry, so
// the lazy chunk-split was already ineffective and only added a Suspense
// fallback flash on first paint. If a future foreign consumer needs the
// chunk-split back, they should provide their own pre-rendered tile shape
// rather than re-introducing lazy() here.

export interface OrdoAppLink {
  id: string
  name: string
  href: string
  description: string | null
  /** Admin-managed self-contained tile SVG (48×48 with bg + icon baked in).
   *  When present, surfaces should render this directly instead of building
   *  a tile from `icon` + `bgColor` + `fgColor`. */
  image: string | null
  /** Pre-rendered icon node. Built from the multipass-managed Phosphor name
   *  (`AppShortcut.icon`); falls back to a generic `<AppWindow />` when the
   *  name is null or unknown. */
  icon: ReactNode
  /** The registry's Phosphor icon name (`AppShortcut.icon`), for surfaces
   *  that need the icon outside React (the tab favicon). */
  iconName: string | null
  /** Hex tile background color (multipass `App.bgColor`). */
  bgColor: string | null
  /** Hex tile icon color (multipass `App.fgColor`). */
  fgColor: string | null
  /** A complete square brand tile (e.g. the Ordo "o" mark) that tile
   *  renderers show as-is instead of building one from the registry's icon. */
  logo?: ReactNode
}

interface AppShortcutWire {
  id: string
  name: string
  href: string
  image: string | null
  description: string | null
  icon: string | null
  bgColor: string | null
  fgColor: string | null
}

const DEFAULT_HQ_URL = 'https://hq.ordo.com'

// `null` until a host calls `setOrdoAppsHqUrl`. Resolved lazily (see
// `hqUrl()`) rather than at module init: `sameEnvOrigin` needs
// `location`, which does not exist during SSR of the Next.js hosts.
let configuredHqUrl: string | null = null

// Production HQ, retargeted onto the current preview env when the page
// is served from one — otherwise every preview app's switcher fetches
// PRODUCTION `/api/apps` and 401s (the preview session cookie is scoped
// to the env's own parent domain). No per-app wiring needed.
function hqUrl(): string {
  return configuredHqUrl ?? sameEnvOrigin(DEFAULT_HQ_URL)
}

// Bound how long a loaded list is trusted before a fresh mount refetches.
// The primary freshness path is the explicit `refreshOrdoApps()` invalidation
// fired by HQ's admin Apps page, which reaches every subscriber inside HQ's
// own bundle instantly. The TTL is the secondary, cross-bundle safety net: an
// app whose tab was left open elsewhere (Tasker, Boombox, …) never receives
// that call, so it self-heals on its next sidebar remount once the list is
// this stale. Matches HQ's auth-store `CACHE_TTL_MS` for consistency.
const TTL_MS = 5 * 60 * 1000

interface OrdoAppsState {
  apps: OrdoAppLink[]
  isLoading: boolean
}

// Module-level store shared by every `useOrdoApps` caller in the bundle, so
// the app list is fetched once and reused across mounts — and, critically,
// can be invalidated in place with all mounted subscribers re-rendering.
// Mirrors the singleton + `Set` of listeners + `notify()` pattern in
// `../pwa-install-capture.ts`.
let state: OrdoAppsState = { apps: [], isLoading: true }
// 0 = never loaded or explicitly invalidated (always triggers a reload).
let cachedAt = 0
let inFlight: Promise<void> | null = null
const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) {
    listener()
  }
}

// Replace `state` with a NEW object (never mutate in place) so `useState`
// consumers see a changed reference and re-render.
function setState(next: OrdoAppsState): void {
  state = next
  notify()
}

function load(): Promise<void> {
  if (inFlight) {
    return inFlight
  }
  // Keep the existing list visible while refetching (SWR-style) — only show a
  // loading state on the very first, empty load so refreshes don't flash.
  if (state.apps.length === 0 && !state.isLoading) {
    setState({ ...state, isLoading: true })
  }
  inFlight = fetchOrdoApps()
    .then((apps) => {
      cachedAt = Date.now()
      setState({ apps, isLoading: false })
    })
    .catch(() => {
      setState({ ...state, isLoading: false })
    })
    .finally(() => {
      inFlight = null
    })
  return inFlight
}

// Load on first use and whenever the cached list has gone stale.
function ensureFresh(): void {
  if (cachedAt === 0 || Date.now() - cachedAt > TTL_MS) {
    void load()
  }
}

// Returning to a tab is the moment a launcher is most likely stale: an
// admin may have re-branded or re-ordered apps in HQ meanwhile, and that
// `refreshOrdoApps()` call only reaches HQ's own bundle. Refetch on return
// once the list is a little old — cheap (one small GET), and it makes an
// HQ edit show up in every open app without a reload.
const FOCUS_REFRESH_MS = 30 * 1000
let focusListening = false

function refreshIfStaleOnReturn(): void {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return
  }
  if (cachedAt !== 0 && Date.now() - cachedAt > FOCUS_REFRESH_MS) {
    void load()
  }
}

function listenForReturn(): void {
  if (focusListening || typeof window === 'undefined') {
    return
  }
  focusListening = true
  window.addEventListener('focus', refreshIfStaleOnReturn)
  document.addEventListener('visibilitychange', refreshIfStaleOnReturn)
}

// Invalidate the cached app list and refetch immediately, notifying every
// mounted `useOrdoApps` subscriber. Call this after any mutation to the app
// registry (e.g. HQ's admin Apps page) so launchers update without a reload.
export function refreshOrdoApps(): void {
  cachedAt = 0
  void load()
}

// Apps usually don't need to set this — the default is `https://hq.ordo.com`
// retargeted onto the page's own parent domain (`sameEnvOrigin`), which is
// correct for prod, for preview envs, and for local dev. Override only when
// HQ is somewhere that rule can't derive (a tunnel, a one-off staging host).
export function setOrdoAppsHqUrl(url: string): void {
  configuredHqUrl = url
  cachedAt = 0
  setState({ apps: [], isLoading: true })
}

function resolveImageUrl(image: string): string {
  // HQ stores image paths like "/images/icons/ask.svg". Cross-origin browsers
  // need an absolute URL pointing back at HQ; same-origin (HQ itself) gets the
  // relative path through unchanged.
  if (/^https?:\/\//i.test(image)) {
    return image
  }
  return `${hqUrl()}${image.startsWith('/') ? image : `/${image}`}`
}

// Production hosts whose label is NOT the app's preview slug. Every other
// `<slug>.ordo.com` maps 1:1 and `sameEnvOrigin` handles it.
const PREVIEW_SLUG_BY_PROD_HOST: Record<string, string> = {
  'app.ordo.com': 'ordo',
  'menubuilder.ordo.com': 'menus',
}

/**
 * HQ's registry stores PRODUCTION app URLs. Inside a preview env that would
 * send every launcher click (app rail, ⌘K, HQ's My Apps) out of the env to
 * the prod sign-in page, so retarget them onto the page's own ring. Identity
 * in production, local dev, and for non-Ordo hosts (HubSpot, …). Exported
 * for tests.
 */
export function retargetAppHref(href: string, pageHost?: string): string {
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return href
  }
  const slug = PREVIEW_SLUG_BY_PROD_HOST[url.host]
  if (slug) {
    const origin = sameEnvSlugOrigin(slug, pageHost)
    return origin ? `${origin}${url.pathname === '/' ? '' : url.pathname}${url.search}${url.hash}` : href
  }
  return sameEnvOrigin(href, pageHost)
}

// Apps whose launcher tile is their real brand mark from `../logos` rather
// than the registry's generic Phosphor icon. Ordo = the app.ordo.com "o".
const APP_LOGOS: Record<string, () => ReactNode> = {
  ordo: () => <OrdoAppIcon />,
}

/**
 * The brand tile for an app whose launcher icon is a real logo rather than a
 * registry Phosphor icon (Ordo = the app.ordo.com "o"). Every surface that
 * draws app tiles from the registry should check this first. Exported for
 * hosts that read the registry without `useOrdoApps` (HQ's admin + home).
 */
export function appBrandLogo(appId: string | null | undefined): ReactNode | null {
  return appId ? (APP_LOGOS[appId]?.() ?? null) : null
}

function toAppLink(s: AppShortcutWire): OrdoAppLink {
  const logo = APP_LOGOS[s.id]?.()
  return {
    ...(logo ? { logo } : {}),
    id: s.id,
    name: s.name,
    href: retargetAppHref(s.href),
    description: s.description,
    image: s.image ? resolveImageUrl(s.image) : null,
    icon: <PhosphorIcon name={s.icon} fallback={<AppWindow />} />,
    iconName: s.icon,
    bgColor: s.bgColor,
    fgColor: s.fgColor,
  }
}

async function fetchOrdoApps(): Promise<OrdoAppLink[]> {
  // The API is the access-control boundary — surface exactly what HQ
  // returns and nothing more. No synthesized HQ link, no offline registry
  // fallback: if the call fails or the response is empty, the consumer
  // should render an empty switcher (or, for `AppSidebarAppSwitcher`,
  // collapse to a non-toggle brand row).
  try {
    // `no-store` bypasses the endpoint's `Cache-Control: private, max-age=60`
    // so an explicit `refreshOrdoApps()` returns the just-changed list rather
    // than a browser-cached body. The module store above is now the single
    // source of client-side caching, so per-fetch HTTP caching only muddied
    // the invalidation story.
    const res = await fetch(`${hqUrl()}/api/apps`, {
      credentials: 'include',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) {
      return []
    }
    const data = (await res.json()) as { apps?: AppShortcutWire[] }
    // No client-side sort: HQ returns the admin-set launcher order
    // (HQ → Apps → Sort), and re-sorting here would undo it everywhere.
    return (data.apps ?? []).map(toAppLink)
  } catch {
    return []
  }
}

// `useSyncExternalStore` binding for the module store. `getSnapshot` returns
// the stable `state` reference (only ever replaced in `setState`), so React
// re-renders exactly when the app list changes and never loops. Subscribing is
// also the "someone wants this data" signal, so `ensureFresh()` lives there.
// Mirrors the subscribe/getSnapshot triple in `../hooks/use-dark-mode.ts`.
export function getOrdoAppsSnapshot(): OrdoAppsState {
  return state
}

export function subscribeOrdoApps(listener: () => void): () => void {
  listeners.add(listener)
  ensureFresh()
  listenForReturn()
  return () => {
    listeners.delete(listener)
  }
}

export function useOrdoApps(): { apps: OrdoAppLink[]; isLoading: boolean } {
  return useSyncExternalStore(subscribeOrdoApps, getOrdoAppsSnapshot, getOrdoAppsSnapshot)
}
